import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { preRegistrationApi } from '../api/pre-registration.api';
import { getApiErrorMessage, getApiErrorStatus } from '../utils/api-error.utils';
import { preRegistrationKeys } from './usePreRegistrationQueries';
import type { MunicipalListPayload } from '../types/pre-registration.types';

const showError = (title: string, error: unknown, fallback: string) =>
  toast({ title, description: getApiErrorMessage(error, fallback), variant: 'destructive' });

export const useCreateMunicipalList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MunicipalListPayload) => preRegistrationApi.createMunicipalList(payload),
    onSuccess: (list) => {
      queryClient.setQueryData(preRegistrationKeys.municipalListDetail(list.id), list);
      queryClient.invalidateQueries({ queryKey: preRegistrationKeys.municipalLists() });
      toast({ title: 'Listado creado', description: 'Ya podés cargar los alojamientos.' });
    },
    onError: (error) =>
      showError('Error al crear el listado', error, 'No se pudo crear el listado municipal.'),
  });
};

export const useUpdateMunicipalList = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MunicipalListPayload) =>
      preRegistrationApi.updateMunicipalList(id, payload),
    onSuccess: (list) => {
      queryClient.setQueryData(preRegistrationKeys.municipalListDetail(id), list);
      queryClient.invalidateQueries({ queryKey: preRegistrationKeys.municipalLists() });
      // Rows embed the municipality of their list
      queryClient.invalidateQueries({ queryKey: preRegistrationKeys.accommodations() });
      toast({ title: 'Listado actualizado' });
    },
    onError: (error) =>
      showError('Error al guardar el listado', error, 'No se pudo guardar el listado municipal.'),
  });
};

export const useDeleteMunicipalList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preRegistrationApi.deleteMunicipalList(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: preRegistrationKeys.municipalListDetail(id) });
      queryClient.invalidateQueries({ queryKey: preRegistrationKeys.municipalLists() });
      toast({ title: 'Listado eliminado' });
    },
    onError: (error, id) => {
      // 409: some row was notified meanwhile; refetch so the button shows why it is disabled
      if (getApiErrorStatus(error) === 409) {
        queryClient.invalidateQueries({ queryKey: preRegistrationKeys.municipalListDetail(id) });
      }
      showError('Error al eliminar el listado', error, 'No se pudo eliminar el listado municipal.');
    },
  });
};

export const useNotifyFiscalizacion = (listId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => preRegistrationApi.notifyFiscalizacion(listId),
    onSuccess: (result) => {
      toast({
        title: 'Se notificó a Fiscalización',
        description: `${result.notifiedCount} ${
          result.notifiedCount === 1 ? 'alojamiento notificado' : 'alojamientos notificados'
        }.`,
      });
    },
    onError: (error) =>
      showError(
        'Error al notificar a Fiscalización',
        error,
        'No se pudo enviar la notificación. Intentá nuevamente.'
      ),
    // On 409 someone else may have just notified: reload in every case
    onSettled: () => queryClient.invalidateQueries({ queryKey: preRegistrationKeys.municipalLists() }),
  });
};
